"use client";

import { c as _c } from "react/compiler-runtime";
import { LazyMotion, domMax, m } from "framer-motion";
import Image from "next/image";
import Link, { useLinkStatus } from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import type { MouseEvent } from "react";
import { useEffect } from "react";
import { useTitle } from "@/contexts/TitleContext";
import { useUnreadIndicator } from "@/hooks/useUnreadIndicator";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useAuth } from "../auth/Auth";
import { useSeizeConnectContext } from "../auth/SeizeConnectContext";
import { useNotificationsContext } from "../notifications/NotificationsContext";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { isNavItemActive } from "./isNavItemActive";
import { getActiveViewFromUrl, useViewContext } from "./ViewContext";
import type { NavItem as NavItemData } from "./navTypes";
interface Props {
  readonly item: NavItemData;
  readonly isCurrentWaveDm?: boolean;
  readonly fullPrefetch?: boolean;
}
const iconSlotClass = "tw-mt-4 tw-flex tw-h-9 tw-items-center tw-justify-center";
const ActiveNavIndicator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <LazyMotion features={domMax}><m.div layoutId="nav-indicator" className="tw-absolute tw-left-0 tw-top-0 tw-h-0.5 tw-w-full tw-rounded-full tw-bg-white" /></LazyMotion>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const NavItemLinkContent = t0 => {
  const $ = _c(25);
  const {
    hasUnreadMessages,
    haveUnreadNotifications,
    icon,
    iconSizeClass,
    isActive,
    item
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const isHighlighted = isActive || pending;
  let t1;
  if ($[0] !== isActive) {
    t1 = isActive && <ActiveNavIndicator />;
    $[0] = isActive;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isActive || $[3] !== pending) {
    t2 = pending && !isActive && <div aria-hidden="true" data-testid="nav-item-pending-indicator" className="tw-absolute tw-left-0 tw-top-0 tw-h-0.5 tw-w-full tw-rounded-full tw-bg-white/60" />;
    $[2] = isActive;
    $[3] = pending;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== icon || $[6] !== iconSizeClass || $[7] !== isHighlighted || $[8] !== item.iconComponent || $[9] !== item.name) {
    t3 = item.iconComponent ? <item.iconComponent className={`${iconSizeClass} ${isHighlighted ? "tw-text-white" : "tw-text-iron-500"}`} /> : <Image src={icon} alt={item.name} width={24} height={24} unoptimized={true} className={iconSizeClass} />;
    $[5] = icon;
    $[6] = iconSizeClass;
    $[7] = isHighlighted;
    $[8] = item.iconComponent;
    $[9] = item.name;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== haveUnreadNotifications || $[12] !== item.name) {
    t4 = item.name === "Notifications" && haveUnreadNotifications && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[11] = haveUnreadNotifications;
    $[12] = item.name;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== hasUnreadMessages || $[15] !== item.name) {
    t5 = item.name === "Messages" && hasUnreadMessages && <div className="tw-absolute -tw-right-1 -tw-top-1 tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-red" />;
    $[14] = hasUnreadMessages;
    $[15] = item.name;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <div className={`tw-relative ${iconSlotClass}`}>{t3}{t4}{t5}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t6) {
    t7 = <>{t1}{t2}{t6}</>;
    $[21] = t1;
    $[22] = t2;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};
const NavItemContent = t0 => {
  const $ = _c(60);
  const {
    item,
    isCurrentWaveDm: t1,
    fullPrefetch: t2
  } = t0;
  const isCurrentWaveDm = t1 === undefined ? false : t1;
  const fullPrefetch = t2 === undefined ? false : t2;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeWaveId = getActiveWaveIdFromUrl({
    pathname,
    searchParams
  });
  const activeView = getActiveViewFromUrl({
    activeWaveId,
    searchParams
  });
  const {
    getNavHref,
    recordNavClick
  } = useViewContext();
  const {
    name
  } = item;
  const {
    icon
  } = item;
  const {
    address,
    seizeConnect
  } = useSeizeConnectContext();
  const {
    connectedProfile
  } = useAuth();
  let t3;
  if ($[0] !== connectedProfile?.handle || $[1] !== connectedProfile?.normalised_handle) {
    t3 = (connectedProfile?.normalised_handle ?? connectedProfile?.handle)?.toLowerCase();
    $[0] = connectedProfile?.handle;
    $[1] = connectedProfile?.normalised_handle;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const normalizedConnectedHandle = t3;
  let t4;
  if ($[3] !== address) {
    t4 = address?.toLowerCase();
    $[3] = address;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const normalizedConnectedAddress = t4;
  const profileSlug = normalizedConnectedHandle ?? normalizedConnectedAddress;
  const profileHref = profileSlug ? `/${profileSlug}` : null;
  const {
    setTitle
  } = useTitle();
  const {
    notifications,
    haveUnreadNotifications
  } = useUnreadNotifications(item.name === "Notifications" ? connectedProfile?.handle ?? null : null);
  const t5 = item.name === "Messages" ? connectedProfile?.handle ?? null : null;
  let t6;
  if ($[5] !== t5) {
    t6 = {
      type: "messages",
      handle: t5
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const {
    hasUnread: hasUnreadMessages
  } = useUnreadIndicator(t6);
  const {
    removeAllDeliveredNotifications
  } = useNotificationsContext();
  let t7;
  if ($[7] !== haveUnreadNotifications || $[8] !== item.name || $[9] !== notifications?.unread_count || $[10] !== removeAllDeliveredNotifications || $[11] !== setTitle) {
    t7 = () => {
      if (item.name !== "Notifications") {
        return;
      }
      if (haveUnreadNotifications) {
        const unreadNotificationsCount = notifications?.unread_count ?? 0;
        setTitle(`(${unreadNotificationsCount}) Notifications | 6529.io`);
      }
      if (!haveUnreadNotifications) {
        removeAllDeliveredNotifications();
        setTitle("Notifications | 6529.io");
      }
    };
    $[7] = haveUnreadNotifications;
    $[8] = item.name;
    $[9] = notifications?.unread_count;
    $[10] = removeAllDeliveredNotifications;
    $[11] = setTitle;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = notifications?.unread_count;
  let t9;
  if ($[13] !== haveUnreadNotifications || $[14] !== item.name || $[15] !== removeAllDeliveredNotifications || $[16] !== setTitle || $[17] !== t8) {
    t9 = [haveUnreadNotifications, item.name, t8, removeAllDeliveredNotifications, setTitle];
    $[13] = haveUnreadNotifications;
    $[14] = item.name;
    $[15] = removeAllDeliveredNotifications;
    $[16] = setTitle;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  useEffect(t7, t9);
  if (item.disabled) {
    let t10;
    if ($[19] !== icon || $[20] !== item.iconComponent || $[21] !== item.iconSizeClass || $[22] !== name) {
      t10 = <div className={iconSlotClass}>{item.iconComponent ? <item.iconComponent className={`${item.iconSizeClass ?? "tw-size-7"} tw-text-iron-500`} /> : <Image src={icon} alt={name} width={24} height={24} unoptimized={true} className={item.iconSizeClass ?? "tw-size-7"} />}</div>;
      $[19] = icon;
      $[20] = item.iconComponent;
      $[21] = item.iconSizeClass;
      $[22] = name;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== name || $[25] !== t10) {
      t11 = <button type="button" aria-label={name} aria-disabled="true" disabled={true} className="tw-pointer-events-none tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-opacity-40 tw-transition-colors focus:tw-outline-none">{t10}</button>;
      $[24] = name;
      $[25] = t10;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  const iconSizeClass = item.iconSizeClass ?? "tw-size-7";
  const isProfileItem = item.kind === "route" && item.name === "Profile";
  const normalizedPathname = pathname.toLowerCase();
  const isProfileActive = isProfileItem && activeView === null && profileHref !== null && (normalizedPathname === profileHref || normalizedPathname.startsWith(`${profileHref}/`));
  const isActive = isProfileItem ? isProfileActive : isNavItemActive(item, pathname, searchParams, activeView, isCurrentWaveDm);
  let t10;
  if ($[27] !== isProfileItem || $[28] !== item || $[29] !== profileHref) {
    t10 = isProfileItem && profileHref !== null ? {
      ...item,
      href: profileHref
    } : item;
    $[27] = isProfileItem;
    $[28] = item;
    $[29] = profileHref;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const resolvedItem = t10;
  let t11;
  if ($[31] !== getNavHref || $[32] !== resolvedItem) {
    t11 = getNavHref(resolvedItem);
    $[31] = getNavHref;
    $[32] = resolvedItem;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const href = t11;
  let t12;
  if ($[34] !== address || $[35] !== item.kind || $[36] !== item.name || $[37] !== recordNavClick || $[38] !== resolvedItem || $[39] !== seizeConnect) {
    t12 = event => {
      if (item.kind === "route" && item.name === "Profile" && !address) {
        event.preventDefault();
        seizeConnect();
        return;
      }
      recordNavClick(resolvedItem);
    };
    $[34] = address;
    $[35] = item.kind;
    $[36] = item.name;
    $[37] = recordNavClick;
    $[38] = resolvedItem;
    $[39] = seizeConnect;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const handleClick = t12;
  let t13;
  if ($[41] !== hasUnreadMessages || $[42] !== haveUnreadNotifications || $[43] !== icon || $[44] !== iconSizeClass || $[45] !== isActive || $[46] !== item) {
    t13 = <NavItemLinkContent hasUnreadMessages={hasUnreadMessages} haveUnreadNotifications={haveUnreadNotifications} icon={icon} iconSizeClass={iconSizeClass} isActive={isActive} item={item} />;
    $[41] = hasUnreadMessages;
    $[42] = haveUnreadNotifications;
    $[43] = icon;
    $[44] = iconSizeClass;
    $[45] = isActive;
    $[46] = item;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const linkContent = t13;
  if (fullPrefetch) {
    const t14 = isActive ? "page" : undefined;
    let t15;
    if ($[48] !== handleClick || $[49] !== href || $[50] !== linkContent || $[51] !== name || $[52] !== t14) {
      t15 = <Link href={href} aria-label={name} aria-current={t14} onClick={handleClick} prefetch={true} className="tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-transition-colors focus:tw-outline-none">{linkContent}</Link>;
      $[48] = handleClick;
      $[49] = href;
      $[50] = linkContent;
      $[51] = name;
      $[52] = t14;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    return t15;
  }
  const t14 = isActive ? "page" : undefined;
  let t15;
  if ($[54] !== handleClick || $[55] !== href || $[56] !== linkContent || $[57] !== name || $[58] !== t14) {
    t15 = <Link href={href} aria-label={name} aria-current={t14} onClick={handleClick} className="tw-relative tw-flex tw-h-full tw-w-full tw-min-w-0 tw-flex-col tw-items-center tw-justify-start tw-border-0 tw-bg-transparent tw-transition-colors focus:tw-outline-none">{linkContent}</Link>;
    $[54] = handleClick;
    $[55] = href;
    $[56] = linkContent;
    $[57] = name;
    $[58] = t14;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
};
const NavItem = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <NavItemContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default NavItem;